import React, { memo, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  AppBar,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  Divider,
  Icon,
  IconButton,
  Stack,
  Toolbar,
  Typography
} from 'investira.react.components';

import services from '../../../services';

const PDF_VIEWER_FRAGMENT = '#toolbar=0&navpanes=0&scrollbar=0&zoom=125';

const Preview = memo(props => {
  const [pdfUrl, setPdfUrl] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const previewCacheId = props.previewParams?.output_cache_id;
  const previewType = props.previewParams?.type;

  const previewParams = useMemo(() => {
    if (previewCacheId) {
      return {
        output_cache_id: previewCacheId,
        type: previewType || props.type || 'pdf'
      };
    }

    if (props.logID) {
      return {
        output_cache_id: props.logID,
        type: props.type || 'pdf'
      };
    }

    return null;
  }, [previewCacheId, previewType, props.logID, props.type]);

  const downloadFileName = useMemo(() => {
    const xBaseName = props.fileName || props.title || 'preview';
    const xType = props.type || previewParams?.type || 'pdf';

    return xBaseName.toLowerCase().endsWith(`.${xType}`) ? xBaseName : `${xBaseName}.${xType}`;
  }, [previewParams?.type, props.fileName, props.title, props.type]);

  const viewerUrl = pdfUrl ? `${pdfUrl}${PDF_VIEWER_FRAGMENT}` : null;

  function handleClose() {
    if (props.onClose) {
      props.onClose();
    }
  }

  function handleDownload() {
    if (!pdfUrl) {
      return;
    }

    const xLinkElem = document.createElement('a');
    xLinkElem.href = pdfUrl;
    xLinkElem.download = downloadFileName;
    document.body.appendChild(xLinkElem);
    xLinkElem.click();
    xLinkElem.remove();
  }

  useEffect(() => {
    let isActive = true;
    let objectUrl = null;

    if (!props.open || !previewParams) {
      setPdfUrl(null);
      setError(null);
      setIsLoading(false);
      return undefined;
    }

    setIsLoading(true);
    setError(null);

    services.outputs.preview(
      previewParams,
      rRes => {
        if (!isActive) {
          return;
        }

        objectUrl = URL.createObjectURL(rRes.data);
        setPdfUrl(objectUrl);
        setIsLoading(false);
      },
      rErr => {
        if (!isActive) {
          return;
        }

        console.log(rErr);
        setError(rErr);
        setPdfUrl(null);
        setIsLoading(false);
      }
    );

    return () => {
      isActive = false;

      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [previewParams, props.open]);

  return (
    <Dialog
      fullScreen
      open={props.open}
      onClose={handleClose}
      PaperProps={{
        sx: {
          display: 'flex',
          flexDirection: 'column'
        }
      }}>
      <AppBar sx={{ position: 'relative' }}>
        <Toolbar>
          <Typography sx={{ flex: 1, mr: 2 }} variant="h6" component="div" noWrap>
            {props.title || 'Pré-visualização'}
          </Typography>
          <Stack direction="row" spacing={3} sx={{ alignItems: 'center', ml: 'auto' }}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<Icon iconName="download" size={16} />}
              onClick={handleDownload}
              disabled={!pdfUrl || isLoading || Boolean(error)}
              size="small"
              sx={{ px: 2 }}>
              Download
            </Button>
            <Divider orientation="vertical" sx={{ height: '24px' }} />
            <IconButton edge="end" color="primary" onClick={handleClose} aria-label="close">
              <Icon iconName="cancel" size={18} />
            </IconButton>
          </Stack>
        </Toolbar>
      </AppBar>

      <DialogContent
        sx={{
          display: 'flex',
          flexDirection: 'column',
          p: 0,
          flex: 1,
          overflow: 'hidden',
          bgcolor: 'background.default'
        }}>
        {isLoading ? (
          <Stack flex={1} alignItems="center" justifyContent="center">
            <CircularProgress size={28} />
          </Stack>
        ) : error ? (
          <Stack
            flex={1}
            alignItems="center"
            justifyContent="center"
            sx={{ px: 2, textAlign: 'center' }}>
            <Typography variant="body1" color="text.secondary">
              Não foi possível carregar o PDF.
            </Typography>
          </Stack>
        ) : viewerUrl ? (
          <iframe
            title={props.title || 'preview pdf'}
            src={viewerUrl}
            style={{
              width: '100%',
              height: '100%',
              border: 0,
              backgroundColor: '#fff'
            }}
          />
        ) : null}
      </DialogContent>
    </Dialog>
  );
});

Preview.propTypes = {
  fileName: PropTypes.string,
  logID: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onClose: PropTypes.func,
  open: PropTypes.bool,
  previewParams: PropTypes.shape({
    output_cache_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    type: PropTypes.string
  }),
  title: PropTypes.string,
  type: PropTypes.string
};

export default Preview;
